import React, {ReactElement, useEffect, useState} from 'react';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import {DialogActions} from '@material-ui/core';
import {
  createStyles,
  DialogContent,
  makeStyles,
  DialogTitle
} from '@material-ui/core';
// styles
import {DEFAULT_WHITE_COLOR, APP_PRIMARY_COLOR} from 'src/style/variable';
import ButtonLoader from '../ButtonLoader/ButtonLoader';
import {useTranslation} from 'react-i18next';
import Close from '../MyIcons/Close';

import CheckBox from '@material-ui/core/Checkbox';
import {getGuarantorNotifications} from '@shared/services/PatientsServices';
import {useSnackbar} from 'notistack';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import {useSelector} from 'react-redux';
import {AppPermissions} from '@shared/constants/Mapping';

const confirmDialogStyle = makeStyles(() =>
  createStyles({
    dialog: {
      position: 'absolute',
      left: '35%',
      top: '35%'
    },
    confirmButton: {
      backgroundColor: APP_PRIMARY_COLOR,
      color: DEFAULT_WHITE_COLOR,
      borderRadius: '4pt',
      '&:hover': {
        backgroundColor: APP_PRIMARY_COLOR
      }
    },
    title: {
      fontSize: '1.2rem'
    },
    subtitle: {
      fontSize: '1.1rem'
    },
    actionWrapper: {
      padding: '16px 24px 16px 24px'
    },
    description: {
      fontSize: '1rem',
      marginTop: '32px'
    },
    dialogTitleView: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    dialogTitle: {
      lineHeight: '1rem',
      paddingTop: '0.2rem'
    },
    pointer: {
      cursor: 'pointer'
    },
    textMargin: {
      marginTop: '1rem'
    },
    subTitle: {
      lineHeight: '1rem'
    },
    italicStyle: {
      fontStyle: 'italic'
    },
    optedOutMainView: {
      flex: 1,
      display: 'flex',
      alignItems: 'center'
    },
    optedOutCell: {
      flex: 0.5
    },
    optedOutBody: {
      flex: 1,
      paddingTop: '2rem',
      paddingBottom: '2rem'
    }
  })
);

interface optedOutElectronicStatementsProps {
  open: boolean;
  onClose(): void;
  onConfirm(value: boolean, optedOutData: any): void;
  forceUpdate?: boolean;
  clinicId: string | number;
  groupId: string | number;
  guarantorId: string | number;
}
const OptOutElectronicStatements = (
  props: optedOutElectronicStatementsProps
): ReactElement => {
  const {
    open,
    onClose,
    onConfirm,
    forceUpdate,
    clinicId,
    groupId,
    guarantorId
  } = props;

  const { t } = useTranslation();
  const popClasses = confirmDialogStyle();
  const [buttonsDisable, setButtonsDiable] = useState<boolean>(false);

  const { userPermissions } = useSelector((state: any) => state.auth);
  // ** read whether the module is enabled or not ** and create the notifications array **//
  const statementsEnable: boolean = userPermissions?.[groupId]?.['group']?.[AppPermissions.MENU_PERMISSIONS.STATEMENTS];
  const appointmentsEnable: boolean = userPermissions?.[groupId]?.['group']?.[AppPermissions.MENU_PERMISSIONS.APPOINTMENTS];
  const patientRecallEnable: boolean = userPermissions?.[groupId]?.['group']?.[AppPermissions.MENU_PERMISSIONS.PATIENT_RECALL];

  const notificationsPreference = [
    { text: 'All', type: 'all', sms: true, email: true },
  ];
  if(statementsEnable) {
    notificationsPreference.push({ text: 'Statements', type: 'statement', sms: true, email: true })
  }
  if(appointmentsEnable) {
    notificationsPreference.push({ text: 'Appointments', type: 'appointment', sms: true, email: true })
  }
  if(patientRecallEnable) {
    notificationsPreference.push({ text: 'Patient Recall', type: 'patient recall', sms: true, email: true })
  }
  const [
    notificationPreferenceArray,
    setNotificationPreferenceArray
  ]: any = useState(notificationsPreference);

  const { enqueueSnackbar } = useSnackbar();

  const checkNotificationsOptedOut = (
    notificationType: string,
    optedOutArray: any
  ): boolean => {
    let flag = true;

    const optedOut = optedOutArray.filter((item: any) => item.type !== 'all');
    let i = 0;
    while (i < optedOut.length) {
      const element: any = optedOut[i];
      if (element[notificationType] !== true) {
        flag = false;
        break;
      }
      i++;
    }
    return flag;
  };

  const updateUseStatePreference = async () => {
    try {
      const getGuarantorPreference = await getGuarantorNotifications(
        groupId,
        guarantorId,
        clinicId
      );
      const notificationPreference = notificationPreferenceArray;

      if (getGuarantorPreference && getGuarantorPreference?.data?.length) {
        const optedOut: any = [...notificationPreference];

        optedOut.forEach((element: any) => {
          getGuarantorPreference?.data.forEach((notification: any) => {
            if (
              notification.notification_type === element.type &&
              notification.delivery_method === 'sms'
            ) {
              element.sms = notification.receive;
            } else if (
              notification.notification_type === element.type &&
              notification.delivery_method === 'email'
            ) {
              element.email = notification.receive;
            }
          });
        });

        const allSmsOptedOut = checkNotificationsOptedOut('sms', optedOut);
        optedOut[0]['sms'] = allSmsOptedOut;

        const allEmailOptedOut = checkNotificationsOptedOut('email', optedOut);
        optedOut[0]['email'] = allEmailOptedOut;

        setNotificationPreferenceArray(optedOut);
      } else {
        setNotificationPreferenceArray(notificationPreference);
      }
    } catch (err: any) {
      enqueueSnackbar(
        (err && err?.message) || t('PATIENTS.GET_PATIENT_DETAILS_ERROR'),
        SnackBarConfig('e')
      );
    }
  };
  useEffect(() => {
    updateUseStatePreference();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceUpdate]);

  const handleClose = () => {
    setNotificationPreferenceArray([]);
    if (typeof onClose === 'function') {
      setButtonsDiable(false);
      onClose();
    }
  };

  const handleConfirm = (response: boolean) => {
    if (typeof onClose === 'function') {
      const optedOut = notificationPreferenceArray.filter(
        (item: any) => item.type !== 'all'
      );
      onConfirm(response, optedOut);

      // setting button disable to true for 3 seconds
      setButtonsDiable(true);
      setTimeout(() => {
        setButtonsDiable(false);
      }, 3000);
    }
  };

  const renderHeaders = () => {
    return (
      <div className={popClasses.optedOutMainView}>
        <div className={popClasses.optedOutCell} />
        <div className={popClasses.optedOutCell}>{t('COMMON.SMS')}</div>
        <div className={popClasses.optedOutCell}>{t('COMMON.EMAIL')}</div>
      </div>
    );
  };

  const handleOptedOutCheckBoxes = (
    type: string,
    index: number,
    event: any,
    notificationType: string
  ) => {
    const newArray: any = [...notificationPreferenceArray];

    if (type === 'all') {
      newArray.forEach((element: any) => {
        element[notificationType] = event.target.checked;
      });
    } else {
      newArray[index][notificationType] = event.target.checked;
    }

    const flag = checkNotificationsOptedOut(notificationType, newArray);
    newArray[0][notificationType] = flag;

    setNotificationPreferenceArray(newArray);
  };

  const renderOptedOutOptions = (): any => {
    return notificationPreferenceArray.map((item: any, i: any) => {
      return (
        <div key={i} className={popClasses.optedOutMainView}>
          <div className={popClasses.optedOutCell}>{item.text}</div>
          <div className={popClasses.optedOutCell}>
            <CheckBox
              data-test-id="pcl-payments-smsCheckBox"
              onChange={(e: any) => {
                handleOptedOutCheckBoxes(item?.type, i, e, 'sms');
              }}
              checked={item.sms}
            />
          </div>
          <div className={popClasses.optedOutCell}>
            <CheckBox
              data-test-id="pcl-payments-emailCheckBox"
              onChange={(e: any) => {
                handleOptedOutCheckBoxes(item?.type, i, e, 'email');
              }}
              checked={item.email}
            />
          </div>
        </div>
      );
    });
  };

  const renderBody = (): ReactElement => {
    return <div>{renderHeaders()}</div>;
  };
  return (
    <div>
      <Dialog
        open={open}
        onClose={handleClose}
        disableBackdropClick={true}
        disableEscapeKeyDown={true}
        fullWidth={false}
        maxWidth={'sm'}>
        <DialogTitle disableTypography>
          <div className={popClasses.dialogTitleView}>
            <div className={popClasses.dialogTitle}>
              {t('PATIENT_DETAILS_CARD.NOTIFICATION_PREFERENCE')}
            </div>
            <Close
              onClick={() => {
                handleClose();
              }}
              className={popClasses.pointer}
            />
          </div>
        </DialogTitle>
        <DialogContent dividers>
          <p className={popClasses.subTitle}>
            {t('PATIENT_DETAILS_CARD.OPTED_OUT_DIALOG_BODY_TITLE')}
          </p>
          <p
            className={[
              popClasses.subTitle,
              popClasses.textMargin,
              popClasses.italicStyle
            ].join(' ')}>
            {t('PATIENT_DETAILS_CARD.OPTED_OUT_DIALOG_BODY_SUBTITLE')}
          </p>

          <div className={popClasses.optedOutBody}>
            {renderBody()}
            {renderOptedOutOptions()}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleClose()} disabled={buttonsDisable}>
            {t('COMMON.CLOSE')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            className={popClasses.confirmButton}
            onClick={() => handleConfirm(true)}
            disabled={buttonsDisable}>
            {buttonsDisable ? <ButtonLoader /> : t('COMMON.SAVE')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default OptOutElectronicStatements;
